import { useMemo } from 'react';
import { Dimensions, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useColors } from '../../hooks/useColors';
import theme from '../../common/theme';
import { useScale } from '../../hooks/useScale';

const { height } = Dimensions.get('window');

const SettingsScreenStyles = () => {
  const { scale } = useScale();
  const { colors } = useColors();
  const insets = useSafeAreaInsets();

  return useMemo(
    () =>
      StyleSheet.create({
        screen: {
          flex: 1,
          height: height,
        },
        header: {
          padding: scale(16),
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: colors.tertiary2,
          paddingTop: scale(16 + insets.top),
        },
        buttons: {
          flexDirection: 'row',
        },
        title: {
          width: scale(400),
          height: scale(46),
          padding: scale(4),
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: theme.radius.m,
          backgroundColor: colors.tertiary3,
        },
        titleText: {
          color: colors.white,
          fontSize: scale(14),
          lineHeight: scale(22),
          paddingVertical: scale(8),
          fontFamily: theme.fonts.medium,
        },
        scrollView: {
          height: height - 78,
        },
        content: {
          alignItems: 'center',
          backgroundColor: colors.elevation0,
        },
        section: {
          // Stretches to the content pane. Used by KitchenTimerSettings, which
          // renders inside a panel.
          alignSelf: 'stretch',
          marginTop: scale(40),
        },
        subSection: {
          marginLeft: scale(20),
          marginBottom: scale(8),
        },
        // As `subSection`, but for a sub-section that sits directly above
        // another related row rather than closing out a group.
        subSectionTight: {
          marginLeft: scale(20),
          marginBottom: scale(2),
        },
        panel: {
          // No flex: panels are laid out inside the shell's ScrollView, where a
          // flex child would collapse instead of growing to its content height.
          // Capped to a readable, centred column so every settings sub-page has
          // the same width instead of stretching the full content pane.
          width: '100%',
          maxWidth: scale(820),
          alignSelf: 'center',
          paddingTop: scale(24),
          paddingHorizontal: scale(24),
          paddingBottom: scale(40),
        },
        panelSection: {
          // Fills the content pane rather than sitting in a fixed 540px column.
          alignSelf: 'stretch',
          marginBottom: scale(32),
        },
        subheading: {
          fontSize: scale(12),
          color: colors.textLight,
          marginBottom: scale(8),
          letterSpacing: scale(1),
          textTransform: 'uppercase',
          paddingHorizontal: scale(14),
          fontFamily: theme.fonts.medium,
        },
        sectionTitle: {
          fontSize: scale(14),
          color: colors.textLight,
          marginBottom: scale(12),
          paddingHorizontal: scale(14),
        },
        footnote: {
          width: scale(540),
          alignItems: 'center',
          paddingVertical: scale(80),
        },
        footnoteText: {
          opacity: 0.2,
          color: colors.text,
          fontSize: scale(10),
          lineHeight: scale(12),
          marginBottom: scale(10),
          textTransform: 'uppercase',
        },
      }),
    [colors, insets, scale],
  );
};

export default SettingsScreenStyles;
